/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

import { get, post, put, del } from '@/services/api/client';
import type { ApiResponse, PaginatedResponse } from '@/services/api/types';
import type {
  DealListItem,
  DealDetail,
  DealStats,
  DealListStats,
  DealActivity,
  CreateDealRequest,
  UpdateDealRequest,
  MoveStageRequest,
  MarkWonRequest,
  MarkLostRequest,
  AddNoteRequest,
} from '../types/deals';

const BASE = '/venta/deals';

async function getDeals(params: Record<string, unknown>): Promise<PaginatedResponse<DealListItem>> {
  const response = await get<ApiResponse<PaginatedResponse<DealListItem>>>(BASE, { params });
  return response.data;
}

async function getDealStats(): Promise<DealStats> {
  const response = await get<ApiResponse<DealStats>>(`${BASE}/stats`);
  return response.data;
}

async function getDealsByStage(stageId: string): Promise<DealListItem[]> {
  const response = await get<ApiResponse<DealListItem[]>>(`${BASE}/by-stage/${stageId}`);
  return response.data;
}

async function getDeal(id: string): Promise<DealDetail> {
  const response = await get<ApiResponse<DealDetail>>(`${BASE}/${id}`);
  return response.data;
}

async function createDeal(data: CreateDealRequest): Promise<DealDetail> {
  const response = await post<ApiResponse<DealDetail>>(BASE, data);
  return response.data;
}

async function updateDeal(id: string, data: UpdateDealRequest): Promise<DealDetail> {
  const response = await put<ApiResponse<DealDetail>>(`${BASE}/${id}`, data);
  return response.data;
}

async function deleteDeal(id: string): Promise<void> {
  await del<ApiResponse<void>>(`${BASE}/${id}`);
}

async function moveDealStage(id: string, data: MoveStageRequest): Promise<DealDetail> {
  const response = await put<ApiResponse<DealDetail>>(`${BASE}/${id}/move-stage`, data);
  return response.data;
}

async function markDealWon(id: string, data: MarkWonRequest): Promise<DealDetail> {
  const response = await post<ApiResponse<DealDetail>>(`${BASE}/${id}/mark-won`, data);
  return response.data;
}

async function markDealLost(id: string, data: MarkLostRequest): Promise<DealDetail> {
  const response = await post<ApiResponse<DealDetail>>(`${BASE}/${id}/mark-lost`, data);
  return response.data;
}

async function getDealActivities(dealId: string, params: Record<string, unknown>): Promise<PaginatedResponse<DealActivity>> {
  const response = await get<ApiResponse<PaginatedResponse<DealActivity>>>(`${BASE}/${dealId}/activities`, { params });
  return response.data;
}

async function addDealNote(dealId: string, data: AddNoteRequest): Promise<DealActivity> {
  const response = await post<ApiResponse<DealActivity>>(`${BASE}/${dealId}/notes`, data);
  return response.data;
}

export interface AddActivityRequest {
  type: string;
  title: string;
  note?: string;
}

async function addDealActivity(dealId: string, data: AddActivityRequest): Promise<DealActivity> {
  const response = await post<ApiResponse<DealActivity>>(`${BASE}/${dealId}/activities`, data);
  return response.data;
}

async function getDealListStats(): Promise<DealListStats> {
  try {
    const response = await get<ApiResponse<DealStats>>(`${BASE}/stats`);
    const s = response.data;
    return {
      totalPipelineValue: s?.totalPipelineValue ?? 0,
      weightedForecast: s?.weightedPipelineValue ?? 0,
      activeDealsCount: s?.openDeals ?? 0,
      avgDealSize: s?.openDeals ? Math.round(s.totalPipelineValue / s.openDeals) : 0,
    };
  } catch {
    return { totalPipelineValue: 0, weightedForecast: 0, activeDealsCount: 0, avgDealSize: 0 };
  }
}

export const dealService = {
  getDeals,
  getDealStats,
  getDealListStats,
  getDealsByStage,
  getDeal,
  createDeal,
  updateDeal,
  deleteDeal,
  moveDealStage,
  markDealWon,
  markDealLost,
  getDealActivities,
  addDealNote,
  addDealActivity,
};
